import { useSearchParams } from 'react-router-dom'
import { useCallback } from 'react'

export function useQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams()

  // Convert current URLSearchParams into a plain object
  const params = Object.fromEntries(searchParams.entries())

  // Low-level escape hatch: mutate a working copy of the current params.
  const setParams = useCallback((mutate) => {
    setSearchParams((currentParams) => {
      const next = new URLSearchParams(currentParams)
      mutate(next)
      // Avoid no-op navigations that trigger unnecessary re-renders.
      if (next.toString() === currentParams.toString()) {
        return currentParams
      }
      return next
    })
  }, [setSearchParams])

  const updateSearchParams = useCallback((newParams, replace = false) => {
    setSearchParams((currentParams) => {
      const updatedParams = replace
        ? new URLSearchParams()
        : new URLSearchParams(currentParams)

      Object.entries(newParams).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          updatedParams.delete(key)
        } else {
          updatedParams.set(key, String(value))
        }
      })

      // Avoid no-op navigations that trigger unnecessary re-renders.
      if (updatedParams.toString() === currentParams.toString()) {
        return currentParams
      }

      return updatedParams
    })
  }, [setSearchParams])

  // Typed getters -------------------------------------------------------------

  const getParam = useCallback(
    (key, fallback = null) => {
      const value = searchParams.get(key)
      return value === null ? fallback : value
    },
    [searchParams],
  )

  const getBool = useCallback(
    (key, fallback = false) => {
      const value = searchParams.get(key)
      if (value === null) return fallback
      return value === 'true'
    },
    [searchParams],
  )

  const getNumber = useCallback(
    (key, fallback = null) => {
      const value = searchParams.get(key)
      if (value === null || value === '') return fallback
      const num = Number(value)
      return Number.isNaN(num) ? fallback : num
    },
    [searchParams],
  )

  const getList = useCallback(
    (key, separator = ',') => {
      const value = searchParams.get(key)
      return value ? value.split(separator).filter(Boolean) : []
    },
    [searchParams],
  )

  const clearParams = useCallback(
    (keys = []) => {
      setParams((next) => keys.forEach((key) => next.delete(key)))
    },
    [setParams],
  )

  return {
    params,
    searchParams,
    setParams,
    updateSearchParams,
    getParam,
    getBool,
    getNumber,
    getList,
    clearParams,
  }
}
